import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import type { inferRouterOutputs } from "@trpc/server";
import {
  GENERATION_TASK_STATUSES,
  socialPostImageCandidateSelectionStatusSchema,
} from "@runcheetah/shared";
import type { AppRouter } from "@runcheetah/api/router";

import {
  CodeText,
  EmptyState,
  FilterCard,
  InfoTile,
  JsonBlock,
  MoneyValue,
  PageHeader,
  PageState,
  SectionLabel,
  Select,
  StatusPill,
  TableEmptyRow,
  TableShell,
} from "@/components/common";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import { formatNullableDateTimeWithSeconds } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type ImageRegenerationListItem = RouterOutputs["admin"]["imageRegenerations"]["list"][number];
type ImageRegenerationDetail = RouterOutputs["admin"]["imageRegenerations"]["detail"];
type ImageRegenerationAttempt = ImageRegenerationDetail["attempts"][number];
type ImageRegenerationStatus = ImageRegenerationListItem["status"];
type ImageRegenerationMode = NonNullable<ImageRegenerationListItem["mode"]>;
type ImageRegenerationSelectionStatus = NonNullable<ImageRegenerationListItem["selectionStatus"]>;

const LIMIT = 50;
const EMPTY_GENERATION_TASK_ID = "00000000-0000-0000-0000-000000000000";
const DETAIL_SEARCH_PARAM = "generationTaskId";

const REGENERATION_MODES: readonly ImageRegenerationMode[] = ["full", "partial"];
const SELECTION_STATUSES =
  socialPostImageCandidateSelectionStatusSchema.options as readonly ImageRegenerationSelectionStatus[];

const TASK_STATUS_CONFIG: Record<ImageRegenerationStatus, { label: string; className: string }> = {
  queued: {
    label: "Queued",
    className: "border-slate-200 bg-slate-50 text-slate-700",
  },
  processing: {
    label: "Processing",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  done: {
    label: "Done",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  failed: {
    label: "Failed",
    className: "border-red-200 bg-red-50 text-red-700",
  },
};

export const getImageRegenerationSelectionStatusLabel = (
  status: ImageRegenerationSelectionStatus | null | undefined,
): string => {
  switch (status) {
    case "qa_passed":
      return "QA passed";
    case "qa_failed_selected_best":
      return "Selected best available";
    case "qa_unavailable_selected_best":
      return "QA unavailable";
    case "qa_disabled":
      return "QA disabled";
    default:
      return "No selection status";
  }
};

export const getImageRegenerationStatusLabel = (status: ImageRegenerationStatus): string =>
  TASK_STATUS_CONFIG[status].label;

export const buildImageRegenerationDetailSearchParams = (
  current: URLSearchParams,
  generationTaskId: string | null,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  if (generationTaskId) {
    next.set(DETAIL_SEARCH_PARAM, generationTaskId);
  } else {
    next.delete(DETAIL_SEARCH_PARAM);
  }
  return next;
};

const formatDate = formatNullableDateTimeWithSeconds;

const formatBytes = (bytes: number | null): string => {
  if (bytes === null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDimensions = (asset: { width: number | null; height: number | null }): string =>
  asset.width && asset.height ? `${asset.width} x ${asset.height}px` : "dimensions unknown";

const toIso = (value: string): string | undefined => {
  if (!value.trim()) {
    return undefined;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toISOString();
};

const StatusBadge = ({ status }: { status: ImageRegenerationStatus }) => {
  const config = TASK_STATUS_CONFIG[status];
  return <StatusPill label={config.label} className={config.className} />;
};

function SelectionBadge({ status }: { status: ImageRegenerationSelectionStatus | null }) {
  const toneClass =
    status === "qa_passed"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : status === "qa_failed_selected_best" || status === "qa_unavailable_selected_best"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-border bg-muted text-muted-foreground";

  return (
    <StatusPill label={getImageRegenerationSelectionStatusLabel(status)} className={toneClass} />
  );
}

function MetadataLine({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3 text-xs">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-foreground">{value}</span>
    </div>
  );
}

function AssetPreview({
  asset,
  label,
  badges,
}: {
  asset: {
    generatedAssetId: string;
    objectKey: string;
    signedUrl: string;
    mimeType: string;
    width: number | null;
    height: number | null;
    sizeBytes: number | null;
  };
  label: string;
  badges?: React.ReactNode;
}) {
  return (
    <article className="overflow-hidden rounded-2xl border bg-background">
      <a
        href={asset.signedUrl}
        target="_blank"
        rel="noreferrer"
        className="block aspect-[4/3] bg-muted/30 transition-opacity hover:opacity-90"
      >
        <img src={asset.signedUrl} alt={label} loading="lazy" className="size-full object-cover" />
      </a>
      <div className="space-y-2 p-3 text-xs">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-foreground">{label}</div>
            <CodeText className="block truncate">{asset.generatedAssetId}</CodeText>
          </div>
          <Badge variant="outline" className="shrink-0 bg-muted text-[10px]">
            {asset.mimeType.split("/").at(1) ?? asset.mimeType}
          </Badge>
        </div>
        {badges ? <div className="flex flex-wrap gap-1.5">{badges}</div> : null}
        <MetadataLine label="Size" value={formatBytes(asset.sizeBytes)} />
        <MetadataLine label="Dimensions" value={formatDimensions(asset)} />
        <MetadataLine
          label="Object"
          value={
            <a
              href={asset.signedUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono hover:text-foreground"
              title={asset.objectKey}
            >
              {asset.objectKey}
            </a>
          }
        />
      </div>
    </article>
  );
}

function QaSummary({ detail }: { detail: ImageRegenerationDetail }) {
  const qaResult = detail.qaSummary.selectedQaResult;
  const failures = qaResult?.failures ?? [];

  return (
    <div className="space-y-2 rounded-2xl border bg-muted/20 p-3 text-xs">
      <div className="flex flex-wrap gap-1.5">
        <Badge
          variant="outline"
          className={
            detail.qaSummary.qaEnabled
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "bg-muted text-muted-foreground"
          }
        >
          QA {detail.qaSummary.qaEnabled ? "enabled" : "disabled"}
        </Badge>
        <Badge
          variant="outline"
          className={
            detail.qaSummary.qaAvailable
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-800"
          }
        >
          QA {detail.qaSummary.qaAvailable ? "available" : "unavailable"}
        </Badge>
        {detail.qaSummary.retryEnabled ? (
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
            {detail.qaSummary.retryCount} retr{detail.qaSummary.retryCount === 1 ? "y" : "ies"}
          </Badge>
        ) : null}
        {typeof qaResult?.overallScorePct === "number" ? (
          <Badge variant="outline" className="bg-background text-foreground">
            Score {qaResult.overallScorePct}%
          </Badge>
        ) : null}
      </div>

      {qaResult ? (
        <div className="space-y-2">
          <p className="leading-5 text-foreground">{qaResult.rationale}</p>
          <div className="flex flex-wrap gap-1.5">
            {failures.length > 0 ? (
              failures.map((failure) => (
                <Badge
                  key={failure}
                  variant="outline"
                  className="border-amber-200 bg-amber-50 text-amber-800"
                >
                  {failure}
                </Badge>
              ))
            ) : (
              <Badge
                variant="outline"
                className="border-emerald-200 bg-emerald-50 text-emerald-800"
              >
                no failures
              </Badge>
            )}
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground">No selected QA result was recorded.</p>
      )}
    </div>
  );
}

function AttemptCard({ attempt }: { attempt: ImageRegenerationAttempt }) {
  return (
    <div className="space-y-3 rounded-2xl border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-foreground">Attempt {attempt.attemptIndex}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {attempt.selected ? (
              <Badge
                variant="outline"
                className="border-emerald-200 bg-emerald-50 text-emerald-800"
              >
                selected
              </Badge>
            ) : null}
            <SelectionBadge status={attempt.selectionStatus} />
            {typeof attempt.qaResult?.overallScorePct === "number" ? (
              <Badge variant="outline" className="bg-muted text-muted-foreground">
                QA {attempt.qaResult.overallScorePct}%
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <CodeText className="block">
            {attempt.selectedGeneratedAssetId?.slice(0, 8) ?? "no winner"}
          </CodeText>
          <div>
            {attempt.assets.length} asset{attempt.assets.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {attempt.selectionReason ? (
        <p className="rounded-md bg-muted/30 px-2 py-1.5 text-xs leading-5 text-foreground">
          {attempt.selectionReason}
        </p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        {attempt.assets.map((asset) => (
          <AssetPreview
            key={asset.generatedAssetId}
            asset={asset}
            label={`${asset.assetVariant ?? "Asset"} ${asset.generatedAssetId.slice(0, 8)}`}
            badges={
              <>
                {asset.regeneration.selected ? (
                  <Badge
                    variant="outline"
                    className="border-emerald-200 bg-emerald-50 text-emerald-800"
                  >
                    selected
                  </Badge>
                ) : null}
                <SelectionBadge status={asset.regeneration.selectionStatus} />
              </>
            }
          />
        ))}
      </div>
    </div>
  );
}

function DetailJsonSections({ detail }: { detail: ImageRegenerationDetail }) {
  return (
    <div className="space-y-2">
      <details className="rounded-lg border bg-background p-3">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Request JSON
        </summary>
        <div className="mt-3">
          <JsonBlock value={detail.rawRequestJson} />
        </div>
      </details>
      <details className="rounded-lg border bg-background p-3">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Result JSON
        </summary>
        <div className="mt-3">
          <JsonBlock value={detail.rawResultJson} />
        </div>
      </details>
    </div>
  );
}

function WorkerRunSummary({ detail }: { detail: ImageRegenerationDetail }) {
  return (
    <div className="space-y-3">
      <SectionLabel>Worker jobs</SectionLabel>
      {detail.workerJobs.length > 0 ? (
        <div className="space-y-2">
          {detail.workerJobs.map((job) => (
            <div key={job.id} className="rounded-lg border bg-background p-3 text-xs">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <CodeText>{job.id}</CodeText>
                <Badge variant="outline" className="bg-muted text-muted-foreground">
                  {job.status}
                </Badge>
              </div>
              <div className="mt-2 grid gap-1 sm:grid-cols-2">
                <MetadataLine label="Attempts" value={`${job.attemptCount}/${job.maxAttempts}`} />
                <MetadataLine label="Priority" value={job.priority} />
                <MetadataLine label="Next run" value={formatDate(job.nextRunAt)} />
                <MetadataLine label="Trace" value={<CodeText>{job.traceId}</CodeText>} />
              </div>
              {job.lastError ? <p className="mt-2 text-destructive">{job.lastError}</p> : null}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState description="No worker jobs found for this regeneration task." />
      )}

      <SectionLabel>Run events</SectionLabel>
      {detail.runEvents.length > 0 ? (
        <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
          {detail.runEvents.map((event) => (
            <div key={event.id} className="rounded-lg border bg-background p-3 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CodeText className="uppercase tracking-wide">{event.eventType}</CodeText>
                <span className="text-muted-foreground">{formatDate(event.at)}</span>
              </div>
              {event.message ? <p className="mt-2 text-foreground">{event.message}</p> : null}
              {Object.keys(event.dataJson).length > 0 ? (
                <div className="mt-2">
                  <JsonBlock value={event.dataJson} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState description="No run events available." />
      )}
    </div>
  );
}

function LlmCostSummary({ detail }: { detail: ImageRegenerationDetail }) {
  return (
    <div className="space-y-3">
      <SectionLabel>LLM cost</SectionLabel>
      <div className="grid gap-2 sm:grid-cols-3">
        <InfoTile label="Total" value={<MoneyValue amount={detail.llmCosts.totalCostUsd} />} />
        <InfoTile label="Events" value={detail.llmCosts.events.length} />
        <InfoTile label="Unpriced" value={detail.llmCosts.unpricedEventCount} />
      </div>
      {detail.llmCosts.groups.length > 0 ? (
        <div className="space-y-2">
          {detail.llmCosts.groups.map((group) => (
            <div key={group.key} className="rounded-lg border bg-background p-3 text-xs">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-foreground">{group.label}</div>
                <MoneyValue amount={group.totalCostUsd} />
              </div>
              <div className="mt-1 text-muted-foreground">
                {group.eventCount} events · {group.unpricedEventCount} unpriced
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DetailPanel({
  generationTaskId,
  fallbackTask,
}: {
  generationTaskId: string | null;
  fallbackTask: ImageRegenerationListItem | null;
}) {
  const { data, isPending, error } = trpc.admin.imageRegenerations.detail.useQuery(
    { generationTaskId: generationTaskId ?? EMPTY_GENERATION_TASK_ID },
    {
      enabled: generationTaskId !== null,
      refetchInterval: (query) => {
        const task = query.state.data;
        return task && (task.status === "queued" || task.status === "processing") ? 2000 : false;
      },
    },
  );

  if (!generationTaskId) {
    return (
      <Card className="min-w-0 xl:sticky xl:top-20 xl:self-start">
        <CardHeader>
          <CardTitle className="text-sm">Regeneration details</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState description="Select a regeneration task to inspect source imagery, selected output, QA, retries, worker events, and cost data." />
        </CardContent>
      </Card>
    );
  }

  if (isPending) {
    return (
      <Card className="min-w-0 xl:sticky xl:top-20 xl:self-start">
        <CardHeader>
          <CardTitle className="text-sm">Regeneration details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="min-w-0 xl:sticky xl:top-20 xl:self-start">
        <CardHeader>
          <CardTitle className="text-sm">Regeneration details</CardTitle>
        </CardHeader>
        <CardContent>
          <PageState title="Failed to load regeneration details" tone="danger" />
        </CardContent>
      </Card>
    );
  }

  const detail = data;
  const ownerCampaignPath = detail.owner.campaignId
    ? `/brands/${detail.brand.id}/campaigns/${detail.owner.campaignId}`
    : null;
  const ownerPostPath =
    ownerCampaignPath && detail.owner.scheduledPostId
      ? `${ownerCampaignPath}/planned-posts/${detail.owner.scheduledPostId}`
      : null;

  return (
    <Card className="min-w-0 xl:sticky xl:top-20 xl:max-h-[calc(100vh-6rem)] xl:self-start xl:overflow-y-auto">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-sm">Regeneration details</CardTitle>
            <CodeText className="mt-1 block truncate">{detail.id}</CodeText>
          </div>
          <StatusBadge status={detail.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-2 sm:grid-cols-2">
          <InfoTile label="Brand" value={detail.brand.sourceDomain ?? detail.brand.id} />
          <InfoTile
            label="Owner"
            value={detail.owner.campaignName ?? detail.owner.standaloneAssetBatchName ?? "Unknown"}
          />
          <InfoTile label="Mode" value={detail.request.mode} />
          <InfoTile label="Block" value={<CodeText>{detail.request.blockId}</CodeText>} />
          <InfoTile label="Created" value={formatDate(detail.createdAt)} />
          <InfoTile label="Updated" value={formatDate(detail.updatedAt)} />
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <Button asChild variant="outline" size="sm">
            <Link to={`/brands/${detail.brand.id}`}>Open brand</Link>
          </Button>
          {ownerCampaignPath ? (
            <Button asChild variant="outline" size="sm">
              <Link to={ownerCampaignPath}>Open campaign</Link>
            </Button>
          ) : null}
          {ownerPostPath ? (
            <Button asChild variant="outline" size="sm">
              <Link to={ownerPostPath}>Open planned post</Link>
            </Button>
          ) : null}
        </div>

        {detail.lastError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {detail.lastError}
          </div>
        ) : null}

        {(fallbackTask?.prompt ?? detail.request.prompt) ? (
          <div className="space-y-1">
            <SectionLabel>Prompt</SectionLabel>
            <p className="max-h-40 overflow-y-auto rounded-lg border bg-muted/20 p-3 text-xs leading-5 whitespace-pre-wrap">
              {detail.request.prompt ?? fallbackTask?.prompt}
            </p>
          </div>
        ) : null}

        <QaSummary detail={detail} />

        <div className="grid gap-3 md:grid-cols-2">
          {detail.sourceAsset ? (
            <AssetPreview asset={detail.sourceAsset} label="Source asset" />
          ) : (
            <div className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
              Source asset unavailable.
            </div>
          )}
          {detail.selectedAsset ? (
            <AssetPreview
              asset={detail.selectedAsset}
              label="Selected regenerated asset"
              badges={<SelectionBadge status={detail.selectedAsset.regeneration.selectionStatus} />}
            />
          ) : (
            <div className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
              No regenerated asset has been selected yet.
            </div>
          )}
        </div>

        <div className="space-y-3">
          <SectionLabel>Attempts ({detail.attempts.length})</SectionLabel>
          {detail.attempts.length > 0 ? (
            detail.attempts.map((attempt) => (
              <AttemptCard key={attempt.attemptIndex} attempt={attempt} />
            ))
          ) : (
            <EmptyState description="No regenerated attempt assets were found." />
          )}
        </div>

        <LlmCostSummary detail={detail} />
        <WorkerRunSummary detail={detail} />
        <DetailJsonSections detail={detail} />
      </CardContent>
    </Card>
  );
}

export function ImageRegenerationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTaskId = searchParams.get(DETAIL_SEARCH_PARAM);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | ImageRegenerationStatus>("");
  const [mode, setMode] = useState<"" | ImageRegenerationMode>("");
  const [selectionStatus, setSelectionStatus] = useState<"" | ImageRegenerationSelectionStatus>("");
  const [qaEnabledFilter, setQaEnabledFilter] = useState<"" | "enabled" | "disabled">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const queryInput = useMemo(
    () => ({
      limit: LIMIT,
      offset,
      search: search.trim() ? search.trim() : undefined,
      status: status || undefined,
      mode: mode || undefined,
      selectionStatus: selectionStatus || undefined,
      qaEnabled:
        qaEnabledFilter === "enabled" ? true : qaEnabledFilter === "disabled" ? false : undefined,
      from: toIso(from),
      to: toIso(to),
    }),
    [offset, search, status, mode, selectionStatus, qaEnabledFilter, from, to],
  );

  const {
    data: tasks,
    isPending,
    error,
  } = trpc.admin.imageRegenerations.list.useQuery(queryInput, {
    refetchInterval: (query) => {
      const rows = query.state.data;
      return rows?.some((row) => row.status === "queued" || row.status === "processing")
        ? 2000
        : false;
    },
  });

  const selectedTask = tasks?.find((task) => task.id === selectedTaskId) ?? null;

  const selectTask = (generationTaskId: string | null) => {
    setSearchParams(buildImageRegenerationDetailSearchParams(searchParams, generationTaskId), {
      replace: false,
    });
  };

  if (isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !tasks) {
    return <PageState title="Failed to load image regeneration tasks" tone="danger" />;
  }

  const hasNextPage = tasks.length === LIMIT;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Image Regenerations"
        meta={`Showing ${tasks.length} regeneration task${tasks.length === 1 ? "" : "s"}`}
      />

      <FilterCard title="Filters" columnsClassName="md:grid-cols-7">
        <Input
          placeholder="Search brand, campaign, task ID, error"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
        />

        <Select
          value={status}
          onChange={(event) => {
            setStatus((event.target.value as ImageRegenerationStatus) || "");
            setOffset(0);
          }}
        >
          <option value="">All statuses</option>
          {GENERATION_TASK_STATUSES.map((item) => (
            <option key={item} value={item}>
              {getImageRegenerationStatusLabel(item)}
            </option>
          ))}
        </Select>

        <Select
          value={mode}
          onChange={(event) => {
            setMode((event.target.value as ImageRegenerationMode) || "");
            setOffset(0);
          }}
        >
          <option value="">All modes</option>
          {REGENERATION_MODES.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>

        <Select
          value={selectionStatus}
          onChange={(event) => {
            setSelectionStatus((event.target.value as ImageRegenerationSelectionStatus) || "");
            setOffset(0);
          }}
        >
          <option value="">All selections</option>
          {SELECTION_STATUSES.map((item) => (
            <option key={item} value={item}>
              {getImageRegenerationSelectionStatusLabel(item)}
            </option>
          ))}
        </Select>

        <Select
          value={qaEnabledFilter}
          onChange={(event) => {
            setQaEnabledFilter((event.target.value as "enabled" | "disabled") || "");
            setOffset(0);
          }}
        >
          <option value="">All QA states</option>
          <option value="enabled">QA enabled</option>
          <option value="disabled">QA disabled</option>
        </Select>

        <Input
          type="datetime-local"
          value={from}
          onChange={(event) => {
            setFrom(event.target.value);
            setOffset(0);
          }}
        />

        <Input
          type="datetime-local"
          value={to}
          onChange={(event) => {
            setTo(event.target.value);
            setOffset(0);
          }}
        />
      </FilterCard>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(420px,0.95fr)]">
        <Card className="min-w-0">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-sm">Regeneration tasks</CardTitle>
              {selectedTaskId ? (
                <Button variant="outline" size="sm" onClick={() => selectTask(null)}>
                  Clear detail
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <TableShell>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>QA / selection</TableHead>
                    <TableHead>Retries</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.length > 0 ? (
                    tasks.map((task) => {
                      const campaignPath = task.campaignId
                        ? `/brands/${task.brandId}/campaigns/${task.campaignId}`
                        : null;
                      return (
                        <TableRow
                          key={task.id}
                          className={task.id === selectedTaskId ? "bg-muted/40" : undefined}
                        >
                          <TableCell className="text-xs text-muted-foreground">
                            {formatDate(task.createdAt)}
                          </TableCell>
                          <TableCell>
                            <div className="flex min-w-0 flex-col">
                              <Link
                                to={`/brands/${task.brandId}`}
                                className="truncate text-sm font-medium hover:underline"
                              >
                                {task.sourceDomain ?? "Unknown brand"}
                              </Link>
                              <CodeText className="text-[11px]">{task.brandId}</CodeText>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex min-w-0 flex-col gap-1">
                              {campaignPath ? (
                                <Link
                                  to={campaignPath}
                                  className="truncate text-sm font-medium hover:underline"
                                >
                                  {task.campaignName ?? "Campaign"}
                                </Link>
                              ) : (
                                <span className="truncate text-sm font-medium">
                                  {task.standaloneAssetBatchName ?? "Standalone batch"}
                                </span>
                              )}
                              <CodeText className="text-[11px]">
                                {task.scheduledPostId ??
                                  task.standaloneAssetBatchId ??
                                  "no owner id"}
                              </CodeText>
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={task.status} />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <CodeText tone="default">{task.mode ?? "unknown"}</CodeText>
                              {task.blockId ? (
                                <CodeText className="text-[11px]">{task.blockId}</CodeText>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1.5">
                              <Badge
                                variant="outline"
                                className={
                                  task.qaEnabled
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                    : "bg-muted text-muted-foreground"
                                }
                              >
                                QA {task.qaEnabled ? "on" : "off"}
                              </Badge>
                              {typeof task.qaOverallScorePct === "number" ? (
                                <Badge variant="outline" className="bg-background text-foreground">
                                  {task.qaOverallScorePct}%
                                </Badge>
                              ) : null}
                              <SelectionBadge status={task.selectionStatus} />
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                              <span>{task.retryEnabled ? "enabled" : "disabled"}</span>
                              <CodeText tone="default">{task.retryCount} retries</CodeText>
                              {task.warningCount > 0 ? (
                                <Badge
                                  variant="outline"
                                  className="w-fit border-amber-200 bg-amber-50 text-amber-800"
                                >
                                  {task.warningCount} warnings
                                </Badge>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell
                            className="max-w-[240px] truncate text-xs text-muted-foreground"
                            title={task.lastError ?? undefined}
                          >
                            {task.lastError ?? "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => selectTask(task.id)}>
                              Inspect
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableEmptyRow colSpan={9} className="p-4">
                        <EmptyState description="No image regeneration tasks match these filters." />
                      </TableEmptyRow>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableShell>

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Offset {offset}</span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((value) => Math.max(0, value - LIMIT))}
                  disabled={offset === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((value) => value + LIMIT)}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <DetailPanel generationTaskId={selectedTaskId} fallbackTask={selectedTask} />
      </div>
    </div>
  );
}
